import { Component, EventEmitter, Input, Output, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';

import { FilterField, Filters } from './filters';
import { FilterService } from './filter.service';
import { TabbedFilterField } from './filters';

import { SavedFilter } from './filter.service';
import { TabFilterTheme } from './filters/tabbed/filter-field-tabbed.component';
import { Breakpoints, BreakpointState, BreakpointObserver } from '@angular/cdk/layout';

@Component({
  selector: 'va-filter-container',
  template: `
    <div class="filter-container">
      <va-filter-tabbed
        *ngFor="let field of tabbedFilters"
        (filterApplied)="handleFilterValueChanged($event)"
        [filterField]="field"
        [theme]="theme"
        [useCustomCase]="useCustomCase"
      ></va-filter-tabbed>
      <div *ngIf="showToolbar" class="toolbar">
        <div class="table-controls-row">
          <ng-container *ngIf="hasSideBarFields()">
            <button
              mat-stroked-button
              class="filter-button"
              (click)="toggleFilterOpen()"
              [ngClass]="{ selected: filterIsOpen && !filtersDisabled }"
              [hidden]="filtersDisabled"
              [disabled]="filtersDisabled"
              data-action="clicked-filter-button"
            >
              <mat-icon>filter_list</mat-icon>
              {{ 'FRONTEND.UIKIT.VA_FILTER.FILTERS' | translate }}
            </button>
          </ng-container>
          <div class="flex-helper">
            <div class="left-toolbar-item">
              <ng-content select="[leftToolbarItem]"></ng-content>
            </div>
          </div>
          <div class="search-and-text">
            <va-search-box
              *ngIf="searchable"
              (update)="searchTermChanged.emit($event)"
              [initial]="initialSearch"
              [disabled]="searchDisabled"
              [enabledSaveSearchTerm]="enabledSaveSearchTerm"
              [contextKey]="contextKey"
              [placeholder]="placeholder"
            ></va-search-box>
            <div class="toolbar-text">{{ toolbarText }}</div>
          </div>
          <ng-content select="[header]"></ng-content>
        </div>
      </div>
      <div class="flex-row">
        <div *ngIf="!filtersDisabled && !!filters" class="filter-wrapper" [ngClass]="{ hide: !filterIsOpen }">
          <va-filter
            [showFilterSharing]="showFilterSharing"
            [filters]="filters"
            [savedFilters]="savedFilters"
            (savedFilterSelected)="savedFilterSelected.emit($event)"
            (saveCurrentFilter)="saveCurrentFilter.emit($event)"
            (removeSavedFilter)="removeSavedFilter.emit($event)"
            (fieldValueChanged)="fieldValueChanged.emit($event)"
            (exportFilterDialog)="exportFilterDialog.emit($event)"
            (importFilterDialog)="importFilterDialog.emit($event)"
          ></va-filter>
        </div>
        <div class="content-container" [ngClass]="{ 'top-border': filters && filters.activeFields.length > 0 }">
          <va-applied-filters-bar
            *ngIf="filters"
            (fieldValueChanged)="handleFilterValueChanged($event)"
            [filters]="filters"
          ></va-applied-filters-bar>
          <div>
            <ng-content select="[content]"></ng-content>
          </div>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['filter-container.component.scss'],
  providers: [FilterService],
})
export class FilterContainerComponent implements OnInit, OnDestroy {
  @Input() showFilterSharing? = false;
  @Input() searchable = true;
  @Input() toolbarText: string;
  @Input() showToolbar = true;
  @Input() searchDisabled = false;
  @Input() filtersDisabled = false;
  @Input() filterIsOpen = false;
  @Input() initialSearch = '';
  @Input() placeholder = '';
  subscriptions: Subscription[] = [];

  @Input() filters: Filters;
  @Input() savedFilters: Observable<SavedFilter[]>;
  @Input() theme: TabFilterTheme = 'gradient';
  @Input() useCustomCase = false;
  @Input() enabledSaveSearchTerm = false;
  @Input() contextKey = '';

  @Output() searchTermChanged = new EventEmitter();
  @Output() fieldValueChanged = new EventEmitter();

  @Output() savedFilterSelected = new EventEmitter();
  @Output() removeSavedFilter = new EventEmitter();
  @Output() saveCurrentFilter = new EventEmitter();
  @Output() exportFilterDialog = new EventEmitter();
  @Output() importFilterDialog = new EventEmitter();

  constructor(private breakPointObserver: BreakpointObserver) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.breakPointObserver.observe(Breakpoints.XSmall).subscribe((state: BreakpointState) => {
        if (state.matches) {
          this.filterIsOpen = false;
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => {
      sub.unsubscribe();
    });
  }

  hasSideBarFields(): boolean {
    if (this.filters != null) {
      return this.filters.fields.filter((field: FilterField<any>) => !(field instanceof TabbedFilterField)).length > 0;
    } else {
      return false;
    }
  }

  toggleFilterOpen(): void {
    if (this.filtersDisabled) {
      return;
    }
    this.filterIsOpen = !this.filterIsOpen;
  }

  get tabbedFilters(): TabbedFilterField[] {
    if (this.filters != null) {
      return this.filters.fields.filter((field) => field instanceof TabbedFilterField) as TabbedFilterField[];
    } else {
      return [];
    }
  }

  handleFilterValueChanged(event: FilterField<any>): void {
    this.fieldValueChanged.emit(event);
  }
}
